/**
 * 高级表格 默认支持选择显示的列, 对列进行排序/调整表格大小等
 */
import {ButtonProps, Card, Table, TableProps} from 'antd';
import ToolBar, {OptionsType} from './Components/ToolBar';
import React, {Key, useMemo, useState} from 'react';
import styles from './index.module.scss';
import {SizeType} from 'antd/es/config-provider/SizeContext';
import {ProTableContext} from './context';
import {ColumnType, TablePaginationConfig} from 'antd/lib/table/interface';
import Operate from './Components/Operate';
import {useUpdateEffect} from 'react-use';
import useStoreColumns from './useStoreColumns';

type OmitProps = 'size';

interface TableState {
    visibleColumnKeys: Key[];
    sortedColumnKeys: Key[];
}

export type ProTableColumnType<RecordType> = {
    hidden?: boolean; // 字段隐藏
    disabled?: boolean; // 字段禁用
} & ColumnType<RecordType>;

export type ProTableColumnsType<RecordType> = ProTableColumnType<RecordType>[];

export interface ProTableOperateType<RecordType>
    extends Omit<ButtonProps, 'onClick'> {
    name: string;
    onClick?(record: RecordType, index: number): void;
}

interface ProTableProps<RecordType>
    extends Omit<TableProps<RecordType>, OmitProps> {
    columns: ProTableColumnsType<RecordType>;
    options?: false | OptionsType; // table 工具栏，设为 false 时不显示
    toolBar?: React.ReactNode; // toolbar自定义区域
    defaultSize?: SizeType; // 默认尺寸
    pagination?: false | TablePaginationConfig;
    defaultTableState?: TableState; // 默认表格状态(用来保存字段隐藏和排序)
    onTableChange?: (data: TableState) => void;
}

export default function ProTable<RecordType extends object>({
    options,
    toolBar,
    defaultSize,
    columns,
    pagination,
    className,
    defaultTableState,
    onTableChange,
    ...restProps
}: ProTableProps<RecordType>) {
    // 把所有列加上key属性
    const localColumns: RequiredKey<ProTableColumnType<RecordType>, 'key'>[] =
        useMemo(() => {
            return columns
                .filter(item => !item.disabled)
                .map((item, index) => ({
                    key: item.key || index,
                    ...item,
                }));
        }, [columns]);

    // 所有列的key
    const allKeys = localColumns.map(item => item.key);

    // 所有默认可见的列
    const defaultVisibleKeys = localColumns
        .filter(item => !item.hidden)
        .map(item => item.key);

    // 表格尺寸
    const [size, setSize] = useState<SizeType>(defaultSize);

    const {
        visibleColumnKeys: defaultVisibleColumnKeys,
        sortedColumnKeys: defaultSortedColumnKeys,
    } = defaultTableState || {};

    // 可见列key
    const [visibleColumnKeys, setVisibleColumnKeys] = useState<Key[]>(
        defaultVisibleColumnKeys || defaultVisibleKeys,
    );

    // 列key排序
    const [sortedColumnKeys, setSortedColumnKeys] = useState<Key[]>(
        defaultSortedColumnKeys || allKeys,
    );

    // 表格树形发生变化时触发事件
    useUpdateEffect(
        () => onTableChange?.({visibleColumnKeys, sortedColumnKeys}),
        [visibleColumnKeys, sortedColumnKeys],
    );

    // 调整过的列
    const adjustedColumns = useMemo(() => {
        return sortedColumnKeys
            .filter(key => visibleColumnKeys.includes(key))
            .map(key => localColumns.find(item => item.key === key)!)
            .filter(item => !!item);
    }, [visibleColumnKeys, sortedColumnKeys, localColumns]);

    // 渲染toolbar
    function renderToolBar() {
        if (toolBar === undefined && options === false) return null;
        return <ToolBar options={options!} toolBar={toolBar} />;
    }

    // 显示分页信息
    function showTotal(total: number, range: [number, number]) {
        return `第 ${range[0]}-${range[1]} 条/总共 ${total} 条`;
    }

    // 分页, 如果是false则默认不显示
    const renderPagination =
        pagination === false
            ? false
            : {
                  showSizeChanger: true,
                  showTotal,
                  ...pagination,
              };
    return (
        <ProTableContext.Provider
            value={{
                size,
                setSize,
                visibleColumnKeys,
                setVisibleColumnKeys,
                sortedColumnKeys,
                setSortedColumnKeys,
                columns: localColumns,
            }}>
            <Card bodyStyle={{paddingTop: 0}} className={className}>
                <div className={styles.header}>{renderToolBar()}</div>

                <Table
                    columns={adjustedColumns}
                    size={size}
                    pagination={renderPagination}
                    {...restProps}
                />
            </Card>
        </ProTableContext.Provider>
    );
}

const defaultProps: ProTableProps<unknown> = {
    options: {setting: false, reload: false, density: true},
    defaultSize: 'large',
    columns: [],
    tableLayout: 'fixed',
    rowKey: 'id',
    sticky: true,
};

ProTable.defaultProps = defaultProps;
ProTable.useStoreColumns = useStoreColumns;

ProTable.Operate = Operate;
